import { existsSync, mkdirSync, readFileSync, renameSync, writeFileSync, chmodSync } from "node:fs";
import { dirname } from "node:path";
import webpush from "web-push";
import type pino from "pino";
import {
  type NotificationEventId,
  type NotificationPreferences,
  NotificationPreferencesSchema,
  shouldDeliverEvent,
} from "../../shared/notification-events.js";

export interface WebPushSubscription {
  endpoint: string;
  keys: {
    p256dh: string;
    auth: string;
  };
}

export interface WebPushEntry {
  subscription: WebPushSubscription;
  /** Stable per-install id from the client. May be absent for legacy entries. */
  deviceId?: string;
  /**
   * Auth-server userId of the connecting session that registered this
   * subscription. `null` for legacy entries written before multi-tenant
   * scoping, and for sessions on a daemon without auth-server linkage. Used
   * to gate per-agent push fan-out so a shared host doesn't leak attention
   * events between users.
   */
  userId: string | null;
  preferences?: NotificationPreferences;
  updatedAt: string;
}

interface VapidKeys {
  publicKey: string;
  privateKey: string;
}

interface PersistedState {
  vapidKey?: string;
  /** New shape — entries with metadata. */
  entries?: WebPushEntry[];
  /** Legacy v0 shape — bare subscriptions. */
  subs?: WebPushSubscription[];
}

/**
 * Store for browser Web Push subscriptions and the VAPID keypair used to
 * authorize them.
 *
 * The VAPID keypair is generated on first run and persisted to disk; if it
 * ever rotates, all existing subscriptions are invalidated (since they were
 * issued against the old key).
 */
export class WebPushStore {
  private readonly logger: pino.Logger;
  private readonly vapidPath: string;
  private readonly subsPath: string;
  private readonly entries: Map<string, WebPushEntry> = new Map();
  private readonly vapid: VapidKeys;

  constructor(logger: pino.Logger, vapidPath: string, subsPath: string) {
    this.logger = logger.child({ component: "web-push-store" });
    this.vapidPath = vapidPath;
    this.subsPath = subsPath;
    this.vapid = this.loadOrCreateVapidKeys();
    this.loadSubscriptions();
  }

  getPublicKey(): string {
    return this.vapid.publicKey;
  }

  getVapidDetails(): { subject: string; publicKey: string; privateKey: string } {
    return {
      subject: "mailto:push@appostle.ohlord.agency",
      publicKey: this.vapid.publicKey,
      privateKey: this.vapid.privateKey,
    };
  }

  /**
   * Upsert a subscription registration. If the entry already exists,
   * deviceId/preferences are merged in (latest write wins for each field).
   */
  upsert(input: {
    subscription: WebPushSubscription;
    deviceId?: string;
    userId?: string | null;
    preferences?: NotificationPreferences;
  }): void {
    const sub = input.subscription;
    if (!sub || !sub.endpoint) return;
    const existing = this.entries.get(sub.endpoint);
    const next: WebPushEntry = {
      subscription: sub,
      deviceId: input.deviceId ?? existing?.deviceId,
      userId: input.userId !== undefined ? input.userId : (existing?.userId ?? null),
      preferences: input.preferences ?? existing?.preferences,
      updatedAt: new Date().toISOString(),
    };
    this.entries.set(sub.endpoint, next);
    this.persist();
    this.logger.debug(
      { total: this.entries.size, deviceId: next.deviceId, userId: next.userId },
      "Upserted web push subscription",
    );
  }

  updatePreferences(args: {
    deviceId?: string;
    endpoint?: string;
    preferences: NotificationPreferences;
  }): boolean {
    const entry = this.findByDeviceOrEndpoint(args);
    if (!entry) return false;
    entry.preferences = args.preferences;
    entry.updatedAt = new Date().toISOString();
    this.persist();
    return true;
  }

  remove(endpoint: string): void {
    if (!endpoint) return;
    if (this.entries.delete(endpoint)) {
      this.persist();
      this.logger.debug({ total: this.entries.size }, "Removed web push subscription");
    }
  }

  removeByDeviceId(deviceId: string): number {
    let removed = 0;
    for (const [endpoint, entry] of this.entries) {
      if (entry.deviceId === deviceId) {
        this.entries.delete(endpoint);
        removed += 1;
      }
    }
    if (removed > 0) this.persist();
    return removed;
  }

  /** Subscriptions whose prefs say they want this event. */
  getSubsForEvent(eventId: NotificationEventId): WebPushSubscription[] {
    const out: WebPushSubscription[] = [];
    for (const entry of this.entries.values()) {
      if (shouldDeliverEvent(entry.preferences, eventId)) {
        out.push(entry.subscription);
      }
    }
    return out;
  }

  /**
   * Same as `getSubsForEvent` but additionally filters by which user is
   * allowed to receive notifications for the agent that triggered the event.
   *
   * Visibility rules:
   *   - Agents with `ownerUserId === null` are unmigrated/corrupt and
   *     receive zero pushes (fail closed).
   *   - An entry receives the push iff `entry.userId === agent.ownerUserId`.
   *   - Legacy entries (`entry.userId === null`) are dropped — the user
   *     must re-subscribe from a session the daemon can resolve.
   */
  getSubsForEventAndAgent(
    eventId: NotificationEventId,
    agent: { ownerUserId: string | null },
  ): WebPushSubscription[] {
    if (agent.ownerUserId === null) return [];
    const ownerUserId: string = agent.ownerUserId;
    const out: WebPushSubscription[] = [];
    for (const entry of this.entries.values()) {
      if (!shouldDeliverEvent(entry.preferences, eventId)) continue;
      if (entry.userId === null) continue;
      if (entry.userId === ownerUserId) {
        out.push(entry.subscription);
      }
    }
    return out;
  }

  getSubsForDevice(deviceId: string): WebPushSubscription[] {
    const out: WebPushSubscription[] = [];
    for (const entry of this.entries.values()) {
      if (entry.deviceId === deviceId) out.push(entry.subscription);
    }
    return out;
  }

  getAll(): WebPushSubscription[] {
    return Array.from(this.entries.values()).map((e) => e.subscription);
  }

  getAllEntries(): WebPushEntry[] {
    return Array.from(this.entries.values());
  }

  private findByDeviceOrEndpoint(args: {
    deviceId?: string;
    endpoint?: string;
  }): WebPushEntry | undefined {
    if (args.endpoint) {
      const direct = this.entries.get(args.endpoint);
      if (direct) return direct;
    }
    if (args.deviceId) {
      for (const entry of this.entries.values()) {
        if (entry.deviceId === args.deviceId) return entry;
      }
    }
    return undefined;
  }

  private loadOrCreateVapidKeys(): VapidKeys {
    try {
      if (existsSync(this.vapidPath)) {
        const raw = readFileSync(this.vapidPath, "utf-8");
        const parsed = JSON.parse(raw) as Partial<VapidKeys>;
        if (parsed.publicKey && parsed.privateKey) {
          return { publicKey: parsed.publicKey, privateKey: parsed.privateKey };
        }
      }
    } catch (error) {
      this.logger.warn({ err: error }, "Failed to load VAPID keys, regenerating");
    }

    const generated = webpush.generateVAPIDKeys();
    try {
      mkdirSync(dirname(this.vapidPath), { recursive: true });
      writeFileSync(this.vapidPath, JSON.stringify(generated, null, 2) + "\n");
      if (process.platform !== "win32") {
        try {
          chmodSync(this.vapidPath, 0o600);
        } catch {
          // best-effort
        }
      }
      this.logger.info("Generated new VAPID keypair");
    } catch (error) {
      this.logger.error({ err: error }, "Failed to persist VAPID keys");
    }
    return generated;
  }

  private loadSubscriptions(): void {
    try {
      if (!existsSync(this.subsPath)) return;
      const raw = readFileSync(this.subsPath, "utf-8");
      const parsed = JSON.parse(raw) as PersistedState;
      // If the VAPID key changed since these were stored, invalidate them all.
      if (parsed.vapidKey && parsed.vapidKey !== this.vapid.publicKey) {
        this.logger.info(
          "VAPID key rotated; discarding previously persisted web push subscriptions",
        );
        this.persist();
        return;
      }

      if (Array.isArray(parsed.entries)) {
        for (const e of parsed.entries) {
          if (!e?.subscription?.endpoint || !e.subscription.keys) continue;
          const prefs = NotificationPreferencesSchema.optional().safeParse(e.preferences);
          this.entries.set(e.subscription.endpoint, {
            subscription: e.subscription,
            deviceId: typeof e.deviceId === "string" ? e.deviceId : undefined,
            userId:
              typeof (e as { userId?: unknown }).userId === "string"
                ? (e as { userId: string }).userId
                : null,
            preferences: prefs.success ? prefs.data : undefined,
            updatedAt: typeof e.updatedAt === "string" ? e.updatedAt : new Date().toISOString(),
          });
        }
      } else if (Array.isArray(parsed.subs)) {
        const now = new Date().toISOString();
        for (const sub of parsed.subs) {
          if (sub && typeof sub.endpoint === "string" && sub.endpoint.length > 0 && sub.keys) {
            // Legacy v0 — no userId available; entry lands as null and is
            // excluded from owned-agent fan-out until re-subscribed.
            this.entries.set(sub.endpoint, { subscription: sub, userId: null, updatedAt: now });
          }
        }
        this.logger.info(
          { migrated: this.entries.size },
          "Migrated legacy web push format to entries",
        );
        this.persist();
      }

      this.logger.info({ total: this.entries.size }, "Loaded web push subscriptions");
    } catch (error) {
      this.logger.warn({ err: error }, "Failed to load web push subscriptions");
    }
  }

  private persist(): void {
    try {
      mkdirSync(dirname(this.subsPath), { recursive: true });
      const tmpPath = `${this.subsPath}.tmp`;
      const payload: PersistedState = {
        vapidKey: this.vapid.publicKey,
        entries: Array.from(this.entries.values()),
      };
      writeFileSync(tmpPath, JSON.stringify(payload, null, 2) + "\n");
      renameSync(tmpPath, this.subsPath);
      if (process.platform !== "win32") {
        try {
          chmodSync(this.subsPath, 0o600);
        } catch {
          // best-effort
        }
      }
    } catch (error) {
      this.logger.warn({ err: error }, "Failed to persist web push subscriptions");
    }
  }
}
